import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BranchesService } from '../../../services/branches.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { EventEmitterService } from '../../../services/event-emitter.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, fromEvent } from 'rxjs';
import { BranchPopupComponent } from '../../../modals/branch-popup/branch-popup.component';
import { DeletePopupComponent } from '../../../modals/delete-popup/delete-popup.component';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '../../../services/authentication.service';
import { AccountTypes } from '../../../models/account_types';

@Component({
  standalone: false,
  selector: 'app-branches',
  templateUrl: './branches.component.html',
  styleUrl: './branches.component.scss',
})
export class BranchesComponent implements OnInit, AfterViewInit {
  user;
  @ViewChild('searchInput') searchInput: ElementRef;
  search = '';
  branches = [];
  page = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 1;
  loading = false;
  destoryRef = inject(DestroyRef);
  columns = [
    { header: 'name', field: 'name' },
    { header: 'phone', field: 'phone' },
    { header: 'location', field: 'location' },
    { header: 'hours', field: 'hours' },
    { header: 'off-days', field: 'offDaysString' },
    { header: 'delivery-type', field: 'delivery_type' },
    { header: 'delivery-radius', field: 'delivery_radius' },
    { header: 'shipping-cost', field: 'shipping_cost' },
  ];

  constructor(
    private authenticationService: AuthenticationService,
    private branchesService: BranchesService,
    private modalService: BsModalService,
    public eventEmitterService: EventEmitterService,
    private translateService: TranslateService,
  ) {
    this.eventEmitterService.invokeEvent
      .pipe(takeUntilDestroyed(this.destoryRef))
      .subscribe((event) => {
        if (event == 'call-all-branches') {
          this.resetPagination();
          this.getAllBranches();
        }
      });
  }

  async ngOnInit() {
    await this.getUser();
    this.setupColumns();
    this.resetPagination();
    this.getAllBranches();
  }

  ngAfterViewInit(): void {
    this.setupSearch();
  }

  async getUser() {
    this.user = await this.authenticationService.getUserFromToken();
  }

  setupColumns() {
    if (this.user.user.business.type == AccountTypes.Delivery) {
      this.columns = [
        { header: 'business', field: 'businessName' },
        { header: 'name', field: 'name' },
        { header: 'phone', field: 'phone' },
        { header: 'location', field: 'location' },
        { header: 'hours', field: 'hours' },
        { header: 'off-days', field: 'offDaysString' },
        { header: 'delivery-type', field: 'delivery_type' },
        { header: 'delivery-radius', field: 'delivery_radius' },
        { header: 'shipping-cost', field: 'shipping_cost' },
      ];
    }
  }

  resetPagination() {
    this.branches = [];
    this.page = 1;
    this.pageSize = 10;
    this.totalItems = 0;
    this.totalPages = 1;
  }

  getAllBranches() {
    if (this.loading) {
      return;
    }
    this.loading = true;

    const apiData: any = {
      page: this.page,
      pageSize: this.pageSize,
    };

    if (this.search && this.search.trim().length > 0) {
      apiData.search = this.search;
    }
    this.branchesService.getAllBranches(apiData).subscribe(
      (response: any) => {
        this.loading = false;

        this.branches = response.items;
        this.page = response.page;
        this.pageSize = response.pageSize;
        this.totalItems = response.totalItems;
        this.totalPages = response.totalPages;

        this.branches.forEach((item) => {
          item.businessName = item.business.name;
          item.location = `[${item.latitude}, ${item.longitude}]`;
          item.hours = `${item.start_hour} - ${item.end_hour}`;
          item.offDaysString = '';
          if (item.off_days && item.off_days.length > 0) {
            if (typeof item.off_days == 'string') {
              item.off_days = JSON.parse(item.off_days);
            }
          }
          if (Array.isArray(item.off_days) && item.off_days.length > 0) {
            item.off_days.forEach((item2, index) => {
              let dayString = this.translateService.instant(item2);
              item.offDaysString = item.offDaysString + dayString;
              if (index + 1 < item.off_days.length) {
                item.offDaysString = item.offDaysString + ', ';
              }
            });
          }
        });

        console.log('this.branches:');
        console.log(this.branches);
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.loading = false;
      },
    );
  }

  setupSearch() {
    fromEvent(this.searchInput.nativeElement, 'input')
      .pipe(debounceTime(3000))
      .subscribe((event: Event) => {
        this.resetPagination();
        this.getAllBranches();
      });
  }

  setSearch(event: any) {
    this.search = event.target.value;
  }

  onPageChange(event: { page: number; pageSize: number }) {
    this.page = event.page;
    this.pageSize = event.pageSize;
    this.getAllBranches();
  }

  addBranch() {
    this.modalService.show(BranchPopupComponent, {
      initialState: {
        title: 'new-branch',
        type: 'new-branch',
      },
      class: 'alert-modal modal-dialog-centered',
      backdrop: 'static',
    });
  }

  editBranch(branch) {
    this.modalService.show(BranchPopupComponent, {
      initialState: {
        title: 'edit-branch',
        type: 'edit-branch',
        branch: branch,
      },
      class: 'alert-modal modal-dialog-centered',
      backdrop: 'static',
    });
  }

  deleteBranchPopup(id) {
    this.modalService.show(DeletePopupComponent, {
      initialState: {
        title: 'delete-branch',
        text: 'delete-branch-text',
        id: id,
        onButtonClick: this.deleteBranch.bind(this),
      },
      class: 'alert-modal modal-dialog-centered',
      backdrop: 'static',
    });
  }

  async deleteBranch(id): Promise<void> {
    console.log('delete branch id:');
    console.log(id);
    return new Promise((resolve, reject) => {
      this.branchesService.deleteBranch(id).subscribe(
        (response) => {
          console.log('response:');
          console.log(response);
          this.eventEmitterService.callAllBranches();
          resolve();
        },
        (error) => {
          console.log('error:');
          console.log(error);
          reject();
        },
      );
    });
  }
}
